/**
 * QuickPasteAddress sub-components and helpers.
 *
 * Extracted from QuickPasteAddress.tsx to keep individual functions within
 * the 50-line / complexity-10 limit.
 */

export type ParseStatus = 'idle' | 'parsing' | 'ai-parsing' | 'done' | 'error'

// =============================================================================
// Status -> label & class lookups
// =============================================================================

export function getParseButtonLabel(status: ParseStatus, didUseAI: boolean): string {
  if (status === 'parsing') { return 'Parsing…' }
  if (status === 'ai-parsing') { return 'AI Parsing…' }
  if (status === 'done') { return didUseAI ? '✓ AI Parsed' : '✓ Done' }
  if (status === 'error') { return 'Partial — check fields' }
  return 'Parse'
}

const STATUS_CLASS_MAP: Record<ParseStatus, (didUseAI: boolean) => string> = {
  idle: () => 'bg-primary text-white hover:bg-primary/90',
  parsing: () => 'bg-primary/60 text-white cursor-wait',
  'ai-parsing': () => 'bg-primary/60 text-white cursor-wait',
  done: (didUseAI) => didUseAI ? 'bg-violet-600 text-white' : 'bg-green-600 text-white',
  error: () => 'bg-amber-500 text-white',
}

export function getParseButtonClass(status: ParseStatus, didUseAI: boolean): string {
  const base = 'px-3 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-60'
  const statusClass = STATUS_CLASS_MAP[status](didUseAI)
  return `${base} ${statusClass}`
}

// =============================================================================
// Button content
// =============================================================================

export function SpinnerIcon(): JSX.Element {
  return (
    <svg className="inline-block animate-spin h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" />
    </svg>
  )
}

// =============================================================================
// Status notices
// =============================================================================

export function StatusNotice({ status }: { status: ParseStatus }): JSX.Element | null {
  if (status === 'ai-parsing') {
    return (
      <p className="mt-1 text-xs text-violet-600 dark:text-violet-400">
        Format not recognised — asking AI to parse…
      </p>
    )
  }
  if (status === 'error') {
    return (
      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
        AI parsing unavailable. Fields partially filled — please check.
      </p>
    )
  }
  return null
}
